import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { apiGet } from "./api.js";

/**
 * Sprint 26 Wave A — feature-flag client for the customer web app.
 *
 * The API resolves Sprint 26 flags at boot from env (`GT_SPRINT26_*`)
 * and surfaces them on `GET /v1/feature-flags` along with a viewer-
 * scoped `visible` boolean. The SPA reads that endpoint once per
 * session (long staleTime) and exposes a tiny hook the rest of the
 * app uses to decide whether to render gated UI.
 *
 * Failure mode: if the endpoint is unreachable, every flag defaults
 * to `visible: false`. The customer app must never show a Sprint 26
 * surface when we can't confirm it's enabled — this is the safe
 * default per the sprint's "no broken visible buttons" rule.
 */

export type Sprint26FlagKey = string;

export interface Sprint26FlagSnapshot {
  readonly key: string;
  readonly wave: string;
  readonly visibility: "public_testable" | "internal_admin_only" | "hidden_disabled";
  readonly enabled: boolean;
  readonly visible: boolean;
  readonly source: "default" | "env";
  readonly description: string;
}

export interface FeatureFlagsResponse {
  readonly sprint: 26;
  readonly resolvedAt: string;
  readonly viewer: {
    readonly roles: readonly string[];
    readonly anonymous: boolean;
  };
  readonly flags: ReadonlyArray<Sprint26FlagSnapshot>;
}

export const FEATURE_FLAGS_KEY = ["feature-flags", "sprint26"] as const;

export function useSprint26FeatureFlags() {
  return useQuery<FeatureFlagsResponse, Error>({
    queryKey: FEATURE_FLAGS_KEY,
    queryFn: ({ signal }) => apiGet<FeatureFlagsResponse>("/v1/feature-flags", undefined, signal),
    // The flag set rarely changes — refetch on window focus only.
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Convenience hook — return whether a single Sprint 26 flag should be
 * rendered for the current viewer. Falls back to `false` while the
 * feature-flag endpoint is loading or errored, which is the safe
 * default: a brief "off" state during boot is preferable to flashing
 * a gated UI before tearing it down.
 */
export function useSprint26FlagVisible(key: string): {
  visible: boolean;
  loading: boolean;
} {
  const { data, isLoading } = useSprint26FeatureFlags();
  return useMemo(() => {
    if (!data) return { visible: false, loading: isLoading };
    // Defensive: tolerate a malformed response (e.g. tests that stub
    // `fetch` with a non-feature-flags body). The safe default for
    // a missing `flags` array is "not visible" — matches the loading
    // / errored / unauthenticated branches above.
    const flags = Array.isArray(data.flags) ? data.flags : [];
    const f = flags.find((flag) => flag.key === key);
    return { visible: f?.visible === true, loading: false };
  }, [data, isLoading, key]);
}

/**
 * Find a flag's full snapshot — useful for admin/QA harness surfaces
 * that render the wave/visibility chip alongside a toggle preview.
 */
export function useSprint26FlagSnapshot(key: string): Sprint26FlagSnapshot | undefined {
  const { data } = useSprint26FeatureFlags();
  return data?.flags.find((f) => f.key === key);
}
